import React, { Fragment, JSX, useState, useEffect, useRef } from "react";
import { Combobox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import { PlusIcon } from "@heroicons/react/24/outline";

// Helper to join class names.
function classNames(...classes: (string | boolean | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

// Define the parameters for the display value function.
interface DisplayValueParams<T> {
  option: T | null;
  accessor: string;
  accessorFn?: (option: T) => string;
}

// Helper to get a display value from an option.
function getOptionDisplayValue<T>({ option, accessor, accessorFn }: DisplayValueParams<T>): string {
  if (!option) return "";
  if (!accessorFn) {
    return (option as any)[accessor] as string;
  }
  return accessorFn(option);
}

// Define the props for the SearchSelect.
export interface SearchSelectProps<T extends { id: string }> {
  options?: T[];
  onSelect: (value: T | null) => void;
  label?: string;
  selectedValue: T | null;
  displayCreationElement?: (visible: boolean) => void;
  creationElement?: React.ReactNode;
  placeholder?: string;
  accessor?: string; // property to use for display (default "name")
  accessorFn?: (option: T) => string;
  loading?: boolean;
  spinnerClassName?: string;
}

export default function SearchSelect<T extends { id: string }>({
  options,
  onSelect,
  label,
  selectedValue,
  displayCreationElement,
  creationElement,
  placeholder,
  accessor = "name",
  accessorFn,
  loading = false,
  spinnerClassName = "h-3 w-3 border-2 border-t-2 border-t-neutral-600 border-neutral-200 rounded-full animate-spin",
}: SearchSelectProps<T>): JSX.Element {
  const [query, setQuery] = useState("");
  const [openOptions, setOpenOptions] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter options based on the query.
  const filteredOptions = query === ""
    ? options
    : options?.filter((e) => {
        const value = (e as any)[accessor];
        return typeof value === "string"
          ? value.toLowerCase().includes(query.toLowerCase())
          : false;
      });

  // A simple skeleton option for loading state.
  const SkeletonOption: React.FC = () => (
    <div className="animate-pulse mx-2 flex items-center justify-center">
      <div className="h-7 bg-neutral-200 dark:bg-neutral-800 rounded w-full py-2 pl-10 pr-4 mx-4"></div>
    </div>
  );

  // Attach a click listener to detect clicks outside the component.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpenOptions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Combobox
        value={selectedValue}
        onChange={(value) => {
          onSelect(value);
          setOpenOptions(false);
        }}
      >
        {({ open }) => (
          <div>
            {label && (
              <Combobox.Label className="block text-sm font-medium leading-6 text-gray-900 w-max">
                {label}
              </Combobox.Label>
            )}
            <div className="relative mt-1">
              <Combobox.Input
                className="w-full rounded-md border border-neutral-300 pl-3 pr-10 py-2 text-left shadow-sm focus:ring-neutral-900 focus:outline-none focus:border-neutral-500 sm:text-sm cursor-pointer placeholder:text-black box-border"
                displayValue={(option: T | null) =>
                  getOptionDisplayValue({ option, accessor, accessorFn })
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
                placeholder={placeholder}
                onClick={() => setOpenOptions(!openOptions)}
              />
              <Combobox.Button
                className="absolute inset-y-0 right-0 flex items-center pr-2"
                onClick={() => setOpenOptions((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
              >
                {loading ? (
                  <span className={spinnerClassName} />
                ) : openOptions ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                )}
              </Combobox.Button>

              <Transition
                show={openOptions}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options
                  className="absolute mt-1 max-h-48 w-full z-[1000] rounded-md bg-white shadow-lg border border-neutral-300 focus:outline-none sm:text-sm flex flex-col overflow-hidden"
                  static={openOptions}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {displayCreationElement && (
                    <div className="border-b border-neutral-300 dark:border-neutral-800 sticky top-0 bg-neutral-100 z-50">
                      <button
                        type="button"
                        className="flex items-center gap-2 w-full py-2 px-4 rounded-md text-neutral-700 hover:bg-neutral-50 hover:text-black dark:hover:bg-neutral-900 dark:hover:text-white"
                        onClick={() => {
                          displayCreationElement(true);
                          setOpenOptions(false);
                        }}
                      >
                        <PlusIcon className="h-4 w-4" strokeWidth={2.5} />
                        {creationElement}
                      </button>
                    </div>
                  )}
                  {options ? (
                    <div className="max-h-full overflow-y-scroll">
                      {filteredOptions?.map((e) => (
                        <Combobox.Option
                          key={e.id}
                          value={e}
                          className={({ active }: { active: boolean }) =>
                            classNames(
                              active ? "bg-neutral-100" : "text-gray-900",
                              "relative cursor-pointer select-none py-2 pl-10 pr-4"
                            )
                          }
                        >
                          {({ active, selected }: { active: boolean; selected: boolean }) => (
                            <>
                              <span
                                className={classNames(
                                  selected ? "font-semibold" : "font-normal",
                                  "block truncate"
                                )}
                              >
                                {getOptionDisplayValue({
                                  option: e,
                                  accessor,
                                  accessorFn,
                                })}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      ))}
                    </div>
                  ) : (
                    <div className="max-h-full overflow-y-scroll space-y-2 mt-1">
                      <SkeletonOption />
                      <SkeletonOption />
                      <SkeletonOption />
                      <SkeletonOption />
                    </div>
                  )}
                </Combobox.Options>
              </Transition>
            </div>
          </div>
        )}
      </Combobox>
    </div>
  );
}
